import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Fallback UI rendered when Sentry.ErrorBoundary catches an unhandled
 * React render error. Matches the visual style of the NotFound page.
 *
 * The error is automatically reported to Sentry by the boundary itself —
 * this component only handles what the user sees.
 */
export const ErrorFallback = ({
  resetError,
}: {
  error: unknown;
  componentStack: string | null;
  eventId: string | null;
  resetError: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-secondary via-background to-muted p-4">
      <div className="text-center space-y-6 max-w-md">
        <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("errorBoundary.title", "Something went wrong")}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "errorBoundary.description",
              "An unexpected error occurred. The issue has been automatically reported.",
            )}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button
            size="lg"
            className="h-touch"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            {t("errorBoundary.goHome", "Go to home page")}
          </Button>
          <Button size="lg" variant="outline" className="h-touch" onClick={resetError}>
            {t("errorBoundary.retry", "Try again")}
          </Button>
        </div>
      </div>
    </div>
  );
};
